/**
 * useSupportTemplate.ts
 */
import Focus from '@shared/types/enums/focus';
import TranslationManager from '@shared/assets/locale/translationsManager';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { popRoute } from '@shared/store/slices/router.slice';
import { selectFocus, setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo } from 'react';

/**
 * Constants
 */
const LITERALS = 'PAGES.SETTINGS.SUPPORT';

/**
 * Hook to use the support template.
 * Handles the support template logic.
 */
export default function useSupportTemplate() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const focus = useAppSelector(selectFocus);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.SUPPORT));
  }, []);

  const onClose = useCallback(() => {
    dispatch(popRoute());
  }, []);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setFocus(Focus.SUPPORT));
  }, []);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  // Support keyboard
  useKeyboard({
    focus: Focus.SUPPORT,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'BACK':
        case 'SELECT':
          dispatch(popRoute());
          break;
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
      }
    },
  });

  // Menu keyboard
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.SUPPORT));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      callbacks: {
        button: onClose,
        container: { onMouseEnter },
      },
      literals: {
        button: TranslationManager.get(`${LITERALS}.BUTTON`),
      },
      props: {
        button: {
          focused: focus === Focus.SUPPORT,
        },
      },
    };
  }, [focus, onClose, onMouseEnter]);
}
